/**
 * Owns the plan edit-lock session and exposes `canEdit`, the single boolean
 * every surface gates on.
 *
 * Deliberately does NOT release on navigate, unmount, or tab close: per product,
 * a user's lock is only given up by an explicit "Exit Edit mode", by the lease
 * lapsing, or by that same user acquiring a lock on a different plan. The
 * heartbeat stops on unmount, so an abandoned session is bounded by one TTL.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { sendNotification } from 'utils/helperMethods';
import { TPOGEN_EDIT_MODE_ENTERED } from 'utils/mixpanelEventNames';

import { trackTpoGenEvent, TPOGEN_PAGE_NAMES } from '../../utils/trackTpoGenEvent';
import { useTpoGenConfig } from '../../context/CustomerConfigContext';
import { tpoGenKeys } from '../../utils/tpoGenQueryKeys';
import { usePlanDetailsContext } from '../context/PlanDetailsContext';
import { useEditLockMeta } from './hooks/queries/useEditLockMeta';
import { useEditLockMutations } from './hooks/mutations/useEditLockMutations';
import { useLockHeartbeat } from './hooks/ui/useLockHeartbeat';
import { useSessionCountdown } from './hooks/ui/useSessionCountdown';
import { EMPTY_SNAPSHOT } from './editLockAdapter';
import { clearLastAcquiredPlan, readLastAcquiredPlan, writeLastAcquiredPlan } from './editLockStorage';
import EditPlanConfirmModal from './components/EditPlanConfirmModal';
import ExtendSessionModal from './components/ExtendSessionModal';
import SessionLostModal from './components/SessionLostModal';
import type { EditSessionValue, LastAcquiredPlanRef } from './types';

/** With the feature off, every surface behaves exactly as it did before. */
const FEATURE_DISABLED_VALUE: EditSessionValue = {
    canEdit: true,
    holder: null,
    isHeldByOther: false,
    secondsRemaining: null,
    isExpiringSoon: false,
    isAcquiring: false,
    isReleasing: false,
    isLockEnabled: false,
    sessionLost: false,
    isEstablishingEditAccess: false,
    acquire: async () => true,
    release: async () => undefined,
    extend: async () => true,
    acknowledgeSessionLost: () => undefined,
    getLastAcquiredPlan: () => null,
    openEditPrompt: () => undefined,
    openExtendPrompt: () => undefined,
};

const CLOSED_EDIT_PROMPT: { open: boolean; otherPlan: LastAcquiredPlanRef | null } = { open: false, otherPlan: null };

/**
 * Safety net for the create-flow auto-acquire: if it never resolves (a failed
 * request, or a response that never lands), the page falls back to whatever
 * the real lock state turns out to be instead of loading forever.
 */
const AUTO_ACQUIRE_TIMEOUT_MS = 6000;

const EditSessionContext = createContext<EditSessionValue>(FEATURE_DISABLED_VALUE);

export const useEditSession = (): EditSessionValue => useContext(EditSessionContext);

interface EditSessionProviderProps {
    planId: string | undefined;
    /** True right after a create-flow auto-acquire was kicked off for this plan. */
    pendingEditAcquire?: boolean;
    children: ReactNode;
}

export const EditSessionProvider = ({ planId, pendingEditAcquire = false, children }: EditSessionProviderProps) => {
    const { editLock } = useTpoGenConfig();
    const { currentPlanDetails } = usePlanDetailsContext();
    const queryClient = useQueryClient();

    const [sessionLost, setSessionLost] = useState(false);
    // The other-plan reference is captured when the prompt opens rather than read
    // during render, so the modal cannot change what it warns about mid-flight.
    const [editPrompt, setEditPrompt] = useState(CLOSED_EDIT_PROMPT);
    const [isExtendPromptOpen, setIsExtendPromptOpen] = useState(false);
    const [isExtending, setIsExtending] = useState(false);
    const [isEstablishingEditAccess, setIsEstablishingEditAccess] = useState(pendingEditAcquire);

    // Distinguishes "the user clicked Exit" from "the server took the lock away",
    // so an intentional release never raises the session-lost modal.
    const isIntentionalReleaseRef = useRef(false);
    const wasMineRef = useRef(false);
    const wasExpiringSoonRef = useRef(false);

    const isLockEnabled = editLock.enabled;

    const { snapshot } = useEditLockMeta({ planId, enabled: isLockEnabled });

    const { acquire: acquireLock, extend: extendLock, release: releaseLock, isAcquiring, isReleasing } =
        useEditLockMutations(planId);

    const isMine = isLockEnabled ? snapshot.isMine : false;
    const planName = currentPlanDetails?.plan_name ?? '';

    const getLastAcquiredPlan = useCallback((): LastAcquiredPlanRef | null => {
        const last = readLastAcquiredPlan();
        // Only meaningful when it points at a DIFFERENT plan than this one.
        return last && last.planId !== planId ? last : null;
    }, [planId]);

    const acquire = useCallback(async (): Promise<boolean> => {
        // Captured before the write below replaces the hint.
        const revokedPlan = getLastAcquiredPlan();
        const acquired = await acquireLock();
        if (acquired && planId) {
            // Acquiring here revoked any lock this user held elsewhere. That
            // plan's cached snapshot still says `isMine`, and the cache outlives
            // this provider, so returning to it would render a live session from
            // stale data until the refetch lands. One lock per user makes the
            // outcome already certain, so write it rather than re-ask.
            if (revokedPlan) queryClient.setQueryData(tpoGenKeys.editLock(revokedPlan.planId), EMPTY_SNAPSHOT);
            writeLastAcquiredPlan(planId, planName);
            setSessionLost(false);
        }
        return acquired;
    }, [acquireLock, getLastAcquiredPlan, planId, planName, queryClient]);

    const release = useCallback(async (): Promise<void> => {
        isIntentionalReleaseRef.current = true;
        const released = await releaseLock();
        if (!released) {
            // Still holding, so any later drop is a genuine loss, not this exit.
            isIntentionalReleaseRef.current = false;
            return;
        }
        if (planId) clearLastAcquiredPlan(planId);
    }, [releaseLock, planId]);

    const extend = useCallback(async (): Promise<boolean> => extendLock(), [extendLock]);

    // The heartbeat only ASKS; the server's response is what keeps this tab in
    // edit mode.
    const requestExtend = useCallback(() => {
        void extendLock();
    }, [extendLock]);

    /**
     * Ends the session the moment the local countdown runs out, rather than
     * asking the server for one more lease. The toast is the only feedback the
     * user gets: `release` flags an intentional exit, which suppresses
     * `SessionLostModal` and would otherwise drop them into read-only silently.
     */
    const handleSessionExpired = useCallback(() => {
        // An unanswered warning prompt now offers to renew a lease that is gone.
        setIsExtendPromptOpen(false);
        sendNotification('info', 'Your edit session has expired.', 5, 450);
        void release();
    }, [release]);

    useLockHeartbeat({ enabled: isMine, ttlSec: snapshot.ttlSec, onBeat: requestExtend });

    const secondsRemaining = useSessionCountdown({
        expiresAtMs: isMine ? snapshot.expiresAtMs : null,
        onReachZero: handleSessionExpired,
    });

    const isExpiringSoon = secondsRemaining !== null && secondsRemaining <= editLock.warningThresholdSec;

    // Prompts once per entry into the warning window. A dismissed prompt stays
    // dismissed until a renewal pushes the lease back out of the window.
    useEffect(() => {
        if (isExpiringSoon && !wasExpiringSoonRef.current) setIsExtendPromptOpen(true);
        wasExpiringSoonRef.current = isExpiringSoon;
    }, [isExpiringSoon]);

    useEffect(() => {
        if (wasMineRef.current && !isMine) {
            if (isIntentionalReleaseRef.current) isIntentionalReleaseRef.current = false;
            else setSessionLost(true);
        }
        wasMineRef.current = isMine;
    }, [isMine]);

    useEffect(() => {
        if (!isEstablishingEditAccess) return;
        if (isMine) {
            setIsEstablishingEditAccess(false);
            return;
        }
        const timeoutId = window.setTimeout(() => setIsEstablishingEditAccess(false), AUTO_ACQUIRE_TIMEOUT_MS);
        return () => window.clearTimeout(timeoutId);
    }, [isEstablishingEditAccess, isMine]);

    const acknowledgeSessionLost = useCallback(() => setSessionLost(false), []);

    const isOperationalPlan = Boolean(currentPlanDetails?.is_operational_plan);

    const openEditPrompt = useCallback(() => {
        if (isOperationalPlan) return;
        setEditPrompt({ open: true, otherPlan: getLastAcquiredPlan() });
    }, [getLastAcquiredPlan, isOperationalPlan]);

    const closeEditPrompt = useCallback(() => setEditPrompt(CLOSED_EDIT_PROMPT), []);

    const confirmEdit = useCallback(async () => {
        const acquired = await acquire();
        if (acquired && planId) {
            trackTpoGenEvent(TPOGEN_EDIT_MODE_ENTERED, TPOGEN_PAGE_NAMES.PLAN_DETAIL_CALENDAR, { plan_id: planId });
        }
        // Closes either way: on refusal the menu already names the live holder.
        setEditPrompt(CLOSED_EDIT_PROMPT);
    }, [acquire, planId]);

    const openExtendPrompt = useCallback(() => setIsExtendPromptOpen(true), []);

    const closeExtendPrompt = useCallback(() => setIsExtendPromptOpen(false), []);

    const confirmExtend = useCallback(async () => {
        setIsExtending(true);
        try {
            await extendLock();
        } finally {
            setIsExtending(false);
            setIsExtendPromptOpen(false);
        }
    }, [extendLock]);

    const value = useMemo<EditSessionValue>(
        () => ({
            canEdit: !isLockEnabled || isMine,
            holder: snapshot.holder,
            isHeldByOther: isLockEnabled && snapshot.isHeldByOther,
            secondsRemaining,
            isExpiringSoon,
            isAcquiring,
            isReleasing,
            isLockEnabled,
            sessionLost,
            isEstablishingEditAccess: isLockEnabled && isEstablishingEditAccess,
            acquire,
            release,
            extend,
            acknowledgeSessionLost,
            getLastAcquiredPlan,
            openEditPrompt,
            openExtendPrompt,
        }),
        [
            isLockEnabled,
            isMine,
            snapshot.holder,
            snapshot.isHeldByOther,
            secondsRemaining,
            isExpiringSoon,
            isAcquiring,
            isReleasing,
            sessionLost,
            isEstablishingEditAccess,
            acquire,
            release,
            extend,
            acknowledgeSessionLost,
            getLastAcquiredPlan,
            openEditPrompt,
            openExtendPrompt,
        ],
    );

    return (
        <EditSessionContext.Provider value={value}>
            {children}
            {isLockEnabled ? (
                <>
                    <EditPlanConfirmModal
                        open={editPrompt.open}
                        planName={planName}
                        otherPlan={editPrompt.otherPlan}
                        isAcquiring={isAcquiring}
                        onCancel={closeEditPrompt}
                        onConfirm={confirmEdit}
                    />
                    <ExtendSessionModal
                        open={isExtendPromptOpen}
                        planName={planName}
                        isExtending={isExtending}
                        onCancel={closeExtendPrompt}
                        onConfirm={confirmExtend}
                    />
                    <SessionLostModal />
                </>
            ) : null}
        </EditSessionContext.Provider>
    );
};

EditSessionProvider.displayName = 'EditSessionProvider';
